import React, { useEffect, useState } from 'react';
import { CreditCard, ExternalLink } from 'lucide-react';
import { useTheme } from '../../lib/theme';
import CurrentPlanCard from '../billing/CurrentPlanCard';
import PlanPicker from '../billing/PlanPicker';
import PaymentHistoryTable from '../billing/PaymentHistoryTable';
import ContactSupportCancelDialog from '../billing/ContactSupportCancelDialog';
import { getPlans, getSubscription, getHistory, createCheckout } from '../../lib/billing';
import { createPortalSession } from '../../lib/api';
import { SectionShell, SettingsCard, primaryBtn, outlineBtn } from './_shared';

export default function BillingSection({ onToast, autoOpenPicker = false }) {
  const { colors } = useTheme();
  const [plans, setPlans] = useState([]);
  const [sub, setSub] = useState(null);
  const [history, setHistory] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [busyPortal, setBusyPortal] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      const [p, s, h] = await Promise.all([
        getPlans().catch(() => []),
        getSubscription().catch(() => null),
        getHistory().catch(() => []),
      ]);
      if (!active) return;
      setPlans(p || []);
      setSub(s || null);
      setHistory(h || []);
    })();
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (autoOpenPicker && sub && !sub.is_admin_override && !sub.plan_code) {
      setPickerOpen(true);
    }
  }, [autoOpenPicker, sub]);

  const isAdmin = !!sub?.is_admin_override;
  const hasSub = !!sub?.plan_code && !isAdmin;

  const onRetryPayment = async () => {
    if (!sub?.plan_code) return;
    try {
      const { url } = await createCheckout(sub.plan_code);
      window.location.assign(url);
    } catch (e) {
      onToast?.(e?.detail || 'Could not start checkout');
    }
  };

  const openPortal = async () => {
    setBusyPortal(true);
    try {
      const { url } = await createPortalSession();
      if (url) window.location.assign(url);
    } catch (e) {
      onToast?.(e?.detail || 'Could not open billing portal');
    } finally {
      setBusyPortal(false);
    }
  };

  const onSwitched = (updated) => {
    setSub({ ...sub, ...updated });
    setPickerOpen(false);
    onToast?.('Plan updated');
  };

  return (
    <SectionShell eyebrow="Billing" title="Billing" description="Plan, payment method, and invoice history." testid="section-billing">
      <CurrentPlanCard
        subscription={sub}
        onChangePlan={() => setPickerOpen(true)}
        onRetryPayment={onRetryPayment}
      />

      {isAdmin ? (
        <SettingsCard
          testid="billing-admin-history"
          title="Payment history"
          description="No charges apply on admin accounts."
        >
          <div style={{ padding: 14, borderRadius: 10, border: `1px dashed ${colors.border}`, color: colors.textMuted, textAlign: 'center', fontSize: 13 }}>
            No payments — admin account.
          </div>
        </SettingsCard>
      ) : (
        <SettingsCard title="Payment history" description="Invoices automatically synced from Stripe." testid="billing-history">
          <PaymentHistoryTable invoices={history} />
        </SettingsCard>
      )}

      <SettingsCard
        title="Change card on file"
        description={isAdmin ? 'Not applicable — admin accounts have no payment method.' : 'Update your card, view invoices, or cancel from the Stripe portal.'}
        testid="billing-portal-card"
      >
        <button
          type="button" onClick={openPortal}
          disabled={isAdmin || !hasSub || busyPortal}
          data-testid="portal-btn"
          style={{ ...primaryBtn(colors, isAdmin || !hasSub || busyPortal), display: 'inline-flex', alignItems: 'center', gap: 8 }}
        >
          <CreditCard size={14} />
          {busyPortal ? 'Opening portal…' : 'Update payment method'}
          {hasSub && !isAdmin && !busyPortal && <ExternalLink size={12} />}
        </button>
        {isAdmin && (
          <div style={{ marginTop: 10, color: colors.textMuted, fontSize: 12 }}>Not applicable for admin.</div>
        )}
        {!isAdmin && !hasSub && (
          <div style={{ marginTop: 10, color: colors.textMuted, fontSize: 12 }}>Start a subscription first to manage your payment method.</div>
        )}
      </SettingsCard>

      {!isAdmin && !hasSub && plans.length > 0 && (
        <SettingsCard title="Pick a plan" description="Start your 14-day free trial. No credit card required." testid="billing-choose-card">
          <button type="button" onClick={() => setPickerOpen(true)} data-testid="choose-plan-btn" style={primaryBtn(colors)}>Choose a plan</button>
        </SettingsCard>
      )}

      {!isAdmin && (
        <SettingsCard title="Request to cancel" description="Cancellations are handled by our team to make sure your data is preserved." testid="billing-cancel-card">
          <button type="button" onClick={() => setCancelOpen(true)} data-testid="cancel-subscription-btn" style={outlineBtn(colors)}>Request to cancel</button>
        </SettingsCard>
      )}

      {pickerOpen && !isAdmin && (
        <PlanPicker
          plans={plans}
          currentPlanCode={sub?.plan_code}
          isAdminOverride={isAdmin}
          onClose={() => setPickerOpen(false)}
          onSwitched={onSwitched}
          onError={(m) => onToast?.(m)}
        />
      )}
      {cancelOpen && <ContactSupportCancelDialog onClose={() => setCancelOpen(false)} />}
    </SectionShell>
  );
}
